import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'shuang-nanzhu');
  const [displayComics, setDisplayComics] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<'popular' | 'recent'>('popular');

  const currentCategory = getCategoryBySlug(activeSlug);

  useEffect(() => {
    if (slug && slug !== activeSlug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    let comics = getComicsByCategory(activeSlug);
    if (sortBy === 'popular') {
      comics = [...comics].sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
    } else {
      comics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    setDisplayComics(comics);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug, sortBy]);

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - 韩漫免费阅读`;
    }
  }, [currentCategory]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`, { replace: true });
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="bg-background-100 py-8">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory?.name || '分类' },
                ]}
              />

              <div className="mt-4">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory?.name || '漫画分类'}
                </h1>
                <p className="mt-2 text-foreground-500 text-sm">{currentCategory?.description || ''}</p>
              </div>
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{displayComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                <button
                  onClick={() => setSortBy('popular')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === 'popular'
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  最受欢迎
                </button>
                <button
                  onClick={() => setSortBy('recent')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === 'recent'
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  最近更新
                </button>
              </div>
            </div>

            {displayComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
                {displayComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-book-2-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无漫画作品</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || '漫画分类'} - 韩漫免费阅读`,
            description: currentCategory?.description || '',
            url: `https://www.example.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫免费阅读',
              url: 'https://www.example.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
                { '@type': 'ListItem', position: 2, name: currentCategory?.name || '分类' },
              ],
            },
          }),
        }}
      />
    </>
  );
}